import {Fragment, type PropsWithChildren, type ReactNode} from 'react'

import type {Author} from './commit-attribution-types'
import {AuthorsDialog} from './components/AuthorsDialog'
import {CommitAuthorStack} from './components/CommitAuthorStack'
import {AuthorLink} from './components/AuthorLink'
import {OrgLink} from './components/OrgLink'
import type {RepositoryNWO} from '@github-ui/current-repository'
import {AuthorAvatar} from './components/AuthorAvatar'
import {AuthorSettingsProvider, type AuthorSettings} from './contexts/AuthorSettingsContext'

import {GitCommitIcon, RepoPushIcon} from '@primer/octicons-react'
import styles from './CommitAttribution.module.css'
import {clsx} from 'clsx'

export interface CommitAttributionProps {
  authors: Author[]
  committer?: Author
  committedDate?: string
  committerAttribution?: boolean
  onBehalfOf?: Author
  includeVerbs: boolean
  repo: RepositoryNWO
  authorSettings?: Partial<AuthorSettings>
  textVariant?: 'default' | 'muted'
  pusher?: Author
  pushedDate?: string
  pushedTime?: ReactNode
  showCommitIcon?: boolean
}

function SingleAuthor({author, repo}: {author: Author; repo: RepositoryNWO}) {
  return <AuthorAvatar author={author} repo={repo} />
}

function AuthorByline({
  author,
  committer,
  committerAttribution,
  onBehalfOf,
  repo,
}: {
  author: Author
  committer?: Author
  committerAttribution?: boolean
  onBehalfOf?: Author
  repo: RepositoryNWO
}) {
  const authors = [author]
  if (committer && committerAttribution) {
    authors.push(committer)
  }

  return (
    <>
      <CommitAuthorStack authors={authors} onBehalfOf={onBehalfOf} />
      <AuthorLink author={author} repo={repo} className={styles.AuthorLink} />
    </>
  )
}

function TwoAuthors({authors, onBehalfOf, repo}: {authors: Author[]; onBehalfOf?: Author; repo: RepositoryNWO}) {
  return (
    <>
      <CommitAuthorStack authors={authors} onBehalfOf={onBehalfOf} />
      {authors.map((author, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={`${author.login}_${index}`}>
          <AuthorLink author={author} repo={repo} className={styles.AuthorLink} />
          {index !== authors.length - 1 && <span className="pl-1">and</span>}
        </Fragment>
      ))}
    </>
  )
}

function MultipleAuthors({authors, onBehalfOf, repo}: {authors: Author[]; onBehalfOf?: Author; repo: RepositoryNWO}) {
  return (
    <>
      <CommitAuthorStack authors={authors} onBehalfOf={onBehalfOf} />
      <AuthorsDialog authors={authors} repo={repo} />
    </>
  )
}

export function CommitAttribution({
  authors,
  committer,
  committedDate,
  committerAttribution,
  onBehalfOf,
  repo,
  children,
  includeVerbs = true,
  authorSettings,
  textVariant = 'default',
  pusher,
  pushedDate,
  pushedTime,
  showCommitIcon = false,
}: PropsWithChildren<CommitAttributionProps>) {
  const singleAuthor = authors.length === 1 && !committerAttribution && !onBehalfOf
  const authorAndCommitter = authors.length === 1 && (committerAttribution || onBehalfOf)
  const inlineAuthorNames = authors.length === 2 && !committerAttribution
  const multipleAuthors = !singleAuthor && !authorAndCommitter && !inlineAuthorNames
  const firstAuthor = authors[0]
  const verbClass = includeVerbs ? 'pl-1' : ''
  const mutedClass = textVariant === 'muted' ? 'color-fg-muted' : ''
  const combinePusherWithCommitter = combineCommitterAndPusher(
    authors,
    pusher,
    committerAttribution,
    pushedDate,
    committedDate,
    onBehalfOf,
  )
  const commitActor = (
    <>
      {singleAuthor && firstAuthor && <SingleAuthor author={firstAuthor} repo={repo} />}
      {authorAndCommitter && firstAuthor && (
        <AuthorByline
          author={firstAuthor}
          committer={committer}
          committerAttribution={committerAttribution}
          onBehalfOf={onBehalfOf}
          repo={repo}
        />
      )}
      {inlineAuthorNames && <TwoAuthors authors={authors} onBehalfOf={onBehalfOf} repo={repo} />}
      {multipleAuthors && <MultipleAuthors authors={authors} onBehalfOf={onBehalfOf} repo={repo} />}
    </>
  )

  return (
    <AuthorSettingsProvider authorSettings={authorSettings}>
      <div className={clsx(mutedClass, styles.CommitAttributionContainer)}>
        {showCommitIcon ? (
          <div className="d-flex flex-nowrap flex-items-center" data-testid="commit-actor">
            <span className="pl-1 pr-1">
              <GitCommitIcon className="mr-1" size="small" aria-label={includeVerbs ? undefined : 'committed'} />
            </span>
            {commitActor}
          </div>
        ) : (
          commitActor
        )}

        <AuthoredOrCommitted
          committer={committer}
          committerAttribution={committerAttribution || false}
          includeVerbs={includeVerbs}
          repo={repo}
          verbClass={verbClass}
        />

        {combinePusherWithCommitter && includeVerbs && <span className="pl-1">and pushed</span>}

        {onBehalfOf && (
          <>
            <span className="pl-1">on behalf of</span>
            <OrgLink org={onBehalfOf} className="pl-1" />
          </>
        )}

        {children}
      </div>

      {pusher && includeVerbs && !combinePusherWithCommitter && (
        <div className={clsx(mutedClass, styles.CommitAttributionContainer)}>
          <PushAttribution pusher={pusher} repo={repo} includeVerbs={includeVerbs}>
            {pushedTime}
          </PushAttribution>
        </div>
      )}
    </AuthorSettingsProvider>
  )
}

function AuthoredOrCommitted({
  committer,
  committerAttribution,
  includeVerbs,
  repo,
  verbClass,
}: {
  committer: Author | undefined
  committerAttribution: boolean
  includeVerbs: boolean
  repo: RepositoryNWO
  verbClass: string
}) {
  if (committer && committer.isGitHub) {
    return <span className={verbClass}>{includeVerbs && 'authored'}</span>
  } else if (!committerAttribution) {
    return <span className={verbClass}>{includeVerbs && 'committed'}</span>
  } else {
    return (
      <>
        <span className="pl-1">{includeVerbs ? 'authored and' : 'and'}</span>
        <AuthorLink author={committer} repo={repo} className={styles.AuthorLink} />
        <span className={verbClass}>{includeVerbs && 'committed'}</span>
      </>
    )
  }
}

export interface PushAttributionProps {
  pusher?: Author
  repo: RepositoryNWO
  includeVerbs?: boolean
  textVariant?: 'default' | 'muted'
}

export function PushAttribution({
  pusher,
  repo,
  children,
  includeVerbs = true,
  textVariant = 'default',
}: PropsWithChildren<PushAttributionProps>) {
  if (!pusher) return null

  const verbClass = includeVerbs ? 'pl-1' : ''

  return (
    <div className={clsx(textVariant === 'muted' ? 'color-fg-muted' : '', styles.CommitAttributionContainer)}>
      <span className="pl-1 pr-1">
        <RepoPushIcon className="mr-1" size="small" aria-label={includeVerbs ? undefined : 'pushed by'} />
      </span>
      <SingleAuthor author={pusher} repo={repo} />
      <span className={verbClass}>{includeVerbs && 'pushed'}</span>
      {children}
    </div>
  )
}

const ONE_MINUTE_MS = 60 * 1000

function pushDateIsCloseToCommitDate(pushedDate?: string | null, committedDate?: string | null): boolean {
  if (!pushedDate || !committedDate) return false

  const pushedAt = new Date(pushedDate).getTime()
  const committedAt = new Date(committedDate).getTime()
  if (Number.isNaN(pushedAt) || Number.isNaN(committedAt)) return false

  return Math.abs(pushedAt - committedAt) < ONE_MINUTE_MS
}

function pusherIsAuthor(author?: Author, pusher?: Author): boolean {
  if (!author?.login || !pusher?.login) return false

  return author.login === pusher.login
}

function combineCommitterAndPusher(
  authors?: Author[],
  pusher?: Author,
  committerAttribution?: boolean,
  pushedDate?: string | null,
  committedDate?: string | null,
  onBehalfOf?: Author,
): boolean {
  if (!authors || !pusher) return false

  if (authors.length > 1) return false

  if (committerAttribution) return false

  if (onBehalfOf) return false

  const pushDateIsClose = pushDateIsCloseToCommitDate(pushedDate, committedDate)
  const pusherAndCommitter = pusherIsAuthor(authors[0], pusher)

  return pushDateIsClose && pusherAndCommitter
}
